
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiCheckCircle,
    FiClock,
    FiEye,
    FiFileText,
    FiLoader,
    FiRefreshCw,
    FiSearch,
    FiSlash,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
} from "react-router-dom";

import {
    cancelDebitNote,
    confirmDebitNote,
    fetchDebitNotes,
    debitNotesSelector,
    debitNoteListSummarySelector,
    debitNotePaginationSelector,
    debitNoteLoadingSelector,
    debitNoteErrorSelector,
} from "../../../../../../store/billing/debitNotes/debitNoteSlice";

import DebitNoteDetailsDialog from "./components/DebitNoteDetailsDialog";

import "./DebitNotes.css";

const PAGE_SIZE_OPTIONS = [
    10,
    20,
    50,
    100,
];

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(
            value
        );

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
        100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",
        }
    ).format(
        date
    );
};

const formatLabel = (
    value
) =>
    String(
        value ||
        "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );

const getDebitAmount = (
    debitNote
) =>
    roundMoney(
        debitNote
            ?.totals
            ?.debitAmount ??
        debitNote
            ?.debitAmount ??
        0
    );

const getSupplierName = (
    debitNote
) =>
    debitNote
        ?.supplierSnapshot
        ?.supplierName ||
    debitNote
        ?.supplierSnapshot
        ?.vendorName ||
    debitNote
        ?.supplier
        ?.supplierName ||
    debitNote
        ?.supplier
        ?.name ||
    "Supplier";

const getSupplierMobile = (
    debitNote
) =>
    debitNote
        ?.supplierSnapshot
        ?.mobile ||
    debitNote
        ?.supplier
        ?.mobile ||
    "-";

const getPurchaseBillId = (
    debitNote
) => {
    const purchaseBill =
        debitNote
            ?.purchaseBill;

    if (
        purchaseBill &&
        typeof purchaseBill ===
        "object"
    ) {
        return (
            purchaseBill?._id ||
            purchaseBill?.id ||
            ""
        );
    }

    return (
        purchaseBill ||
        debitNote
            ?.purchaseBillId ||
        ""
    );
};

const getErrorMessage = (
    error,
    fallback
) =>
    error?.message ||
    error?.payload
        ?.message ||
    error?.payload ||
    error?.response
        ?.data
        ?.message ||
    fallback;

const DebitNoteListPage = () => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    const debitNotes =
        useSelector(
            debitNotesSelector
        ) || [];

    const summary =
        useSelector(
            debitNoteListSummarySelector
        ) || {};

    const pagination =
        useSelector(
            debitNotePaginationSelector
        ) || {};

    const loading =
        useSelector(
            debitNoteLoadingSelector
        ) || {};

    const errors =
        useSelector(
            debitNoteErrorSelector
        ) || {};

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        status,
        setStatus,
    ] = useState("");

    const [
        fromDate,
        setFromDate,
    ] = useState("");

    const [
        toDate,
        setToDate,
    ] = useState("");

    const [
        page,
        setPage,
    ] = useState(1);

    const [
        limit,
        setLimit,
    ] = useState(20);

    const [
        selectedDebitNote,
        setSelectedDebitNote,
    ] = useState(null);

    const [
        detailsOpen,
        setDetailsOpen,
    ] = useState(false);

    const [
        actionDebitNoteId,
        setActionDebitNoteId,
    ] = useState("");

    const [
        localError,
        setLocalError,
    ] = useState("");

    const loadDebitNotes =
        useCallback(
            async () => {
                try {
                    setLocalError(
                        ""
                    );

                    await dispatch(
                        fetchDebitNotes({
                            page,

                            limit,

                            search:
                                search.trim(),

                            status,

                            fromDate,

                            toDate,

                            sortBy:
                                "debitNoteDate",

                            sortOrder:
                                "desc",
                        })
                    ).unwrap();
                } catch (
                error
                ) {
                    setLocalError(
                        getErrorMessage(
                            error,
                            "Unable to load debit notes."
                        )
                    );
                }
            },
            [
                dispatch,
                page,
                limit,
                search,
                status,
                fromDate,
                toDate,
            ]
        );

    useEffect(
        () => {
            const timer =
                setTimeout(
                    () => {
                        loadDebitNotes();
                    },
                    350
                );

            return () =>
                clearTimeout(
                    timer
                );
        },
        [
            loadDebitNotes,
        ]
    );

    const normalizedSummary =
        useMemo(
            () => ({
                total:
                    toNumber(
                        summary
                            ?.total
                    ),

                draft:
                    toNumber(
                        summary
                            ?.draft
                    ),

                confirmed:
                    toNumber(
                        summary
                            ?.confirmed
                    ),

                cancelled:
                    toNumber(
                        summary
                            ?.cancelled
                    ),

                confirmedDebitAmount:
                    roundMoney(
                        summary
                            ?.confirmedDebitAmount
                    ),

                totalDebitAmount:
                    roundMoney(
                        summary
                            ?.totalDebitAmount
                    ),
            }),
            [
                summary,
            ]
        );

    const handleOpenDetails = (
        debitNote
    ) => {
        setSelectedDebitNote(
            debitNote
        );

        setDetailsOpen(
            true
        );
    };

    const handleCloseDetails =
        () => {
            setDetailsOpen(
                false
            );

            setSelectedDebitNote(
                null
            );
        };

    const handleViewPage = (
        debitNote
    ) => {
        if (
            !debitNote?._id
        ) {
            return;
        }

        history.push(
            `/billing/debit-notes/${debitNote._id}`
        );
    };

    const handleViewPurchaseBill = (
        debitNote
    ) => {
        const purchaseBillId =
            getPurchaseBillId(
                debitNote
            );

        if (
            !purchaseBillId
        ) {
            setLocalError(
                "Purchase bill ID is missing."
            );

            return;
        }

        /*
         * Purchase Bills module is not ready yet.
         *
         * Later you can enable:
         *
         * history.push(
         *   `/billing/purchase-bills/${purchaseBillId}`
         * );
         */

        setLocalError(
            "Purchase Bills module is not available yet."
        );
    };

    const handleConfirm =
        async (
            debitNote
        ) => {
            if (
                !debitNote?._id
            ) {
                return;
            }

            const approved =
                window.confirm(
                    `Confirm debit note ${debitNote
                        ?.debitNoteNumber ||
                    ""
                    }?\n\nInventory will be reduced and supplier payable will be adjusted.`
                );

            if (
                !approved
            ) {
                return;
            }

            try {
                setLocalError(
                    ""
                );

                setActionDebitNoteId(
                    debitNote._id
                );

                await dispatch(
                    confirmDebitNote(
                        debitNote._id
                    )
                ).unwrap();

                await loadDebitNotes();
            } catch (
            error
            ) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to confirm debit note."
                    )
                );
            } finally {
                setActionDebitNoteId(
                    ""
                );
            }
        };

    const handleCancel =
        async (
            debitNote
        ) => {
            if (
                !debitNote?._id
            ) {
                return;
            }

            const reason =
                window.prompt(
                    `Enter cancellation reason for ${debitNote
                        ?.debitNoteNumber ||
                    "this debit note"
                    }:`
                );

            if (
                reason ===
                null
            ) {
                return;
            }

            const normalizedReason =
                String(
                    reason ||
                    ""
                ).trim();

            if (
                normalizedReason.length <
                3
            ) {
                setLocalError(
                    "Cancellation reason must contain at least 3 characters."
                );

                return;
            }

            try {
                setLocalError(
                    ""
                );

                setActionDebitNoteId(
                    debitNote._id
                );

                await dispatch(
                    cancelDebitNote({
                        debitNoteId:
                            debitNote._id,

                        cancellationReason:
                            normalizedReason,
                    })
                ).unwrap();

                await loadDebitNotes();
            } catch (
            error
            ) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to cancel debit note."
                    )
                );
            } finally {
                setActionDebitNoteId(
                    ""
                );
            }
        };

    const handleClearFilters =
        () => {
            setSearch(
                ""
            );

            setStatus(
                ""
            );

            setFromDate(
                ""
            );

            setToDate(
                ""
            );

            setPage(
                1
            );
        };

    const totalRecords =
        toNumber(
            pagination
                ?.total
        );

    const totalPages =
        Math.max(
            toNumber(
                pagination
                    ?.totalPages,
                1
            ),
            1
        );

    const currentPage =
        Math.max(
            toNumber(
                pagination
                    ?.page,
                page
            ),
            1
        );

    const startRecord =
        totalRecords >
            0
            ? (
                currentPage -
                1
            ) *
            limit +
            1
            : 0;

    const endRecord =
        Math.min(
            currentPage *
            limit,
            totalRecords
        );

    return (
        <div className="debit-note-list-page">
            <header className="debit-note-list-header">
                <div className="debit-note-list-heading">
                    <span className="debit-note-list-heading-icon">
                        <FiFileText />
                    </span>

                    <div>
                        <h1>
                            Debit Notes
                        </h1>

                        <p>
                            Manage purchase returns and supplier debit adjustments.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    className="debit-note-button"
                    disabled={
                        loading.list
                    }
                    onClick={
                        loadDebitNotes
                    }
                >
                    <FiRefreshCw
                        className={
                            loading.list
                                ? "debit-note-spinner"
                                : ""
                        }
                    />

                    Refresh
                </button>
            </header>

            <section className="debit-note-summary-grid">
                <div className="debit-note-summary-card">
                    <span>
                        Total Debit Notes
                    </span>

                    <strong>
                        {
                            normalizedSummary
                                .total
                        }
                    </strong>

                    <small>
                        All debit notes
                    </small>
                </div>

                <div className="debit-note-summary-card debit-note-summary-card-draft">
                    <span>
                        Draft
                    </span>

                    <strong>
                        {
                            normalizedSummary
                                .draft
                        }
                    </strong>

                    <small>
                        Awaiting confirmation
                    </small>
                </div>

                <div className="debit-note-summary-card debit-note-summary-card-confirmed">
                    <span>
                        Confirmed
                    </span>

                    <strong>
                        {
                            normalizedSummary
                                .confirmed
                        }
                    </strong>

                    <small>
                        Posted returns
                    </small>
                </div>

                <div className="debit-note-summary-card debit-note-summary-card-amount">
                    <span>
                        Confirmed Debit
                    </span>

                    <strong>
                        {formatCurrency(
                            normalizedSummary
                                .confirmedDebitAmount
                        )}
                    </strong>

                    <small>
                        Supplier payable reduced
                    </small>
                </div>
            </section>

            <section className="debit-note-list-toolbar">
                <div className="debit-note-list-search">
                    <FiSearch />

                    <input
                        type="text"
                        value={
                            search
                        }
                        placeholder="Search debit note, purchase bill, supplier or mobile..."
                        onChange={(
                            event
                        ) => {
                            setPage(
                                1
                            );

                            setSearch(
                                event
                                    .target
                                    .value
                            );
                        }}
                    />
                </div>

                <select
                    value={
                        status
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(
                            1
                        );

                        setStatus(
                            event
                                .target
                                .value
                        );
                    }}
                >
                    <option value="">
                        All Status
                    </option>

                    <option value="DRAFT">
                        Draft
                    </option>

                    <option value="CONFIRMED">
                        Confirmed
                    </option>

                    <option value="CANCELLED">
                        Cancelled
                    </option>
                </select>

                <input
                    type="date"
                    value={
                        fromDate
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(
                            1
                        );

                        setFromDate(
                            event
                                .target
                                .value
                        );
                    }}
                />

                <input
                    type="date"
                    value={
                        toDate
                    }
                    min={
                        fromDate ||
                        undefined
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(
                            1
                        );

                        setToDate(
                            event
                                .target
                                .value
                        );
                    }}
                />

                <button
                    type="button"
                    className="debit-note-button"
                    onClick={
                        handleClearFilters
                    }
                >
                    Clear
                </button>
            </section>

            {(localError ||
                errors.list) && (
                    <div className="debit-note-alert">
                        {localError ||
                            errors.list}
                    </div>
                )}

            <section className="debit-note-list-card">
                {loading.list &&
                    debitNotes.length ===
                    0 ? (
                    <div className="debit-note-empty">
                        <FiLoader className="debit-note-spinner" />

                        <h3>
                            Loading debit notes...
                        </h3>
                    </div>
                ) : debitNotes.length ===
                    0 ? (
                    <div className="debit-note-empty">
                        <FiFileText />

                        <h3>
                            No debit notes found
                        </h3>

                        <p>
                            Debit notes will appear here after purchase returns are created.
                        </p>
                    </div>
                ) : (
                    <div className="debit-note-table-wrapper">
                        <table className="debit-note-table">
                            <thead>
                                <tr>
                                    <th>
                                        Debit Note
                                    </th>

                                    <th>
                                        Supplier
                                    </th>

                                    <th>
                                        Purchase Bill
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                    <th>
                                        Reason
                                    </th>

                                    <th className="text-right">
                                        Amount
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th className="text-right">
                                        Actions
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {debitNotes.map(
                                    (
                                        debitNote,
                                        index
                                    ) => {
                                        const debitStatus =
                                            String(
                                                debitNote
                                                    ?.status ||
                                                "DRAFT"
                                            ).toUpperCase();

                                        const processing =
                                            actionDebitNoteId ===
                                            debitNote?._id;

                                        return (
                                            <tr
                                                key={
                                                    debitNote?._id ||
                                                    index
                                                }
                                            >
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="debit-note-list-view-button"
                                                        onClick={() =>
                                                            handleViewPage(
                                                                debitNote
                                                            )
                                                        }
                                                    >
                                                        {debitNote
                                                            ?.debitNoteNumber ||
                                                            "-"}
                                                    </button>
                                                </td>

                                                <td>
                                                    <div className="create-debit-note-item">
                                                        <strong>
                                                            {getSupplierName(
                                                                debitNote
                                                            )}
                                                        </strong>

                                                        <span>
                                                            {getSupplierMobile(
                                                                debitNote
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>

                                                <td>
                                                    <button
                                                        type="button"
                                                        className="debit-note-list-view-button"
                                                        onClick={() =>
                                                            handleViewPurchaseBill(
                                                                debitNote
                                                            )
                                                        }
                                                    >
                                                        {debitNote
                                                            ?.purchaseBillNumber ||
                                                            "-"}
                                                    </button>
                                                </td>

                                                <td>
                                                    {formatDate(
                                                        debitNote
                                                            ?.debitNoteDate
                                                    )}
                                                </td>

                                                <td>
                                                    {formatLabel(
                                                        debitNote
                                                            ?.reason
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    <strong>
                                                        {formatCurrency(
                                                            getDebitAmount(
                                                                debitNote
                                                            )
                                                        )}
                                                    </strong>
                                                </td>

                                                <td>
                                                    <span
                                                        className={`debit-note-status debit-note-status-${debitStatus.toLowerCase()}`}
                                                    >
                                                        {debitStatus ===
                                                            "CONFIRMED" ? (
                                                            <FiCheckCircle />
                                                        ) : debitStatus ===
                                                            "CANCELLED" ? (
                                                            <FiSlash />
                                                        ) : (
                                                            <FiClock />
                                                        )}

                                                        {formatLabel(
                                                            debitStatus
                                                        )}
                                                    </span>
                                                </td>

                                                <td className="text-right">
                                                    <div className="debit-note-list-actions">
                                                        <button
                                                            type="button"
                                                            className="debit-note-list-view-button"
                                                            disabled={
                                                                processing
                                                            }
                                                            onClick={() =>
                                                                handleOpenDetails(
                                                                    debitNote
                                                                )
                                                            }
                                                        >
                                                            <FiEye />

                                                            View
                                                        </button>

                                                        {debitStatus ===
                                                            "DRAFT" && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="debit-note-list-confirm-button"
                                                                        disabled={
                                                                            processing
                                                                        }
                                                                        onClick={() =>
                                                                            handleConfirm(
                                                                                debitNote
                                                                            )
                                                                        }
                                                                    >
                                                                        {processing ? (
                                                                            <FiLoader className="debit-note-spinner" />
                                                                        ) : (
                                                                            <FiCheckCircle />
                                                                        )}

                                                                        Confirm
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="debit-note-list-cancel-button"
                                                                        disabled={
                                                                            processing
                                                                        }
                                                                        onClick={() =>
                                                                            handleCancel(
                                                                                debitNote
                                                                            )
                                                                        }
                                                                    >
                                                                        <FiSlash />
                                                                    </button>
                                                                </>
                                                            )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <footer className="debit-note-pagination">
                    <div>
                        <span>
                            Showing{" "}
                            <strong>
                                {startRecord}
                            </strong>
                            {" - "}
                            <strong>
                                {endRecord}
                            </strong>
                            {" of "}
                            <strong>
                                {totalRecords}
                            </strong>
                        </span>

                        <select
                            value={
                                limit
                            }
                            onChange={(
                                event
                            ) => {
                                setPage(
                                    1
                                );

                                setLimit(
                                    Number(
                                        event
                                            .target
                                            .value
                                    )
                                );
                            }}
                        >
                            {PAGE_SIZE_OPTIONS.map(
                                (
                                    size
                                ) => (
                                    <option
                                        key={
                                            size
                                        }
                                        value={
                                            size
                                        }
                                    >
                                        {size}
                                    </option>
                                )
                            )}
                        </select>
                    </div>

                    <div>
                        <button
                            type="button"
                            disabled={
                                currentPage <=
                                1 ||
                                loading.list
                            }
                            onClick={() =>
                                setPage(
                                    Math.max(
                                        currentPage -
                                        1,
                                        1
                                    )
                                )
                            }
                        >
                            Previous
                        </button>

                        <span>
                            Page{" "}
                            <strong>
                                {currentPage}
                            </strong>{" "}
                            of{" "}
                            <strong>
                                {totalPages}
                            </strong>
                        </span>

                        <button
                            type="button"
                            disabled={
                                currentPage >=
                                totalPages ||
                                loading.list
                            }
                            onClick={() =>
                                setPage(
                                    Math.min(
                                        currentPage +
                                        1,
                                        totalPages
                                    )
                                )
                            }
                        >
                            Next
                        </button>
                    </div>
                </footer>
            </section>

            <DebitNoteDetailsDialog
                open={
                    detailsOpen
                }
                debitNote={
                    selectedDebitNote
                }
                onClose={
                    handleCloseDetails
                }
                onUpdated={
                    loadDebitNotes
                }
            />
        </div>
    );
};

export default DebitNoteListPage;